<template>
  <section>
    <div class="container">
      <div class="row" :class="rowClass">
        <div class="col-12 col-lg-7 px-0">
          <img
            :data-src="imageSource"
            :alt="imageAlt"
            class="mx-auto d-block image-class std-lazy rounded-circle" />
        </div>
        <div class="col-12 col-lg-5">
          <div
            class="px-lg-space-md py-space-lg py-xl-space-xxl"
            :class="[mobileAlignClass, desktopAlignClass]">
            <component
              class="pb-space-md pb-lg-space-lg"
              :is="titleLevel"
              :class="titleClass">
              {{ title }}
            </component>
            <div class="pb-space-xs">
              <img
                :data-src="quote"
                alt="open-quote"
                class="quote-class std-lazy" />
            </div>
            <div
              :class="textClass"
              class="pb-0 pb-space-lg-md"
              v-html="text"></div>
            <hr class="custom-tick bg-secondary mb-space-xs mb-lg-space-md" />
            <div :class="authorClass" v-html="authorText"></div>
            <div :class="designationClass" v-html="designationText"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";
import quote from "./assets/std-quote-icon.svg?url";

interface Props {
  imageSource: string;
  imagePosition?: "left" | "right";
  imageAlt: string;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
  text: string;
  textSize?: "small" | "medium" | "large" | "xl";
  textVariant?: string;
  authorText: string;
  authorTextSize?: "small" | "medium" | "large" | "xl";
  authorTextVariant?: string;
  authorTextWeight?: "bold" | "normal" | "light" | "lighter";
  designationText?: string;
  designationTextSize?: "small" | "medium" | "large" | "xl";
  designationTextVariant?: string;
  textAlignMobile?: "left" | "right" | "center";
  textAlignDesktop?: "left" | "right" | "center";
}

const props = withDefaults(defineProps<Props>(), {
  imagePosition: "left",
  titleLevel: "h2",
  titleSize: "small",
  titleVariant: "dark-3",
  textSize: "medium",
  textVariant: "dark-3",
  authorTextSize: "small",
  authorTextVariant: "dark-3",
  authorTextWeight: "bold",
  designationText: "Lorem Ipsum",
  designationTextSize: "small",
  designationTextVariant: "dark-3",
  textAlignMobile: "center",
  textAlignDesktop: "left",
});

const ctaAlignMap = {
  left: "start",
  center: "center",
  right: "end",
};

const rowClass = computed(() => {
  return props.imagePosition == "left" ? `flex-row` : `flex-row-reverse`;
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const textClass = computed(() => {
  return `fs-${props.textSize} text-${props.textVariant}`;
});

const authorClass = computed(() => {
  return `fs-${props.authorTextSize} text-${props.authorTextVariant} fw-${props.authorTextWeight}`;
});

const designationClass = computed(() => {
  return `fs-${props.designationTextSize} text-${props.designationTextVariant}`;
});

const mobileAlignClass = computed(() => {
  return `text-${ctaAlignMap[props.textAlignMobile]}`;
});

const desktopAlignClass = computed(() => {
  return `text-lg-${ctaAlignMap[props.textAlignDesktop]}`;
});

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    const observer = lozad(".std-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.custom-tick {
  height: 5px;
  width: 15%;
  border-top: none;
  display: inline-block;
}

.quote-class {
  height: 35px;
  width: 48px;
}
.image-class {
  width: 242px;
  height: 242px;
  object-position: top;
  object-fit: cover;
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .image-class {
    width: 520px;
    height: 520px;
  }
}

@media (min-width: 1200px) {
  .image-class {
    width: 635px;
    height: 635px;
    margin-right: 30px;
  }
}
</style>
